import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Loader, fmt, StatusBadge, fmtDateTime, EmptyState } from "@/components/shared";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { StatementExport } from "./StatementExport";
import { GlassSelect } from "@/components/GlassSelect";

const types = ["all", "deposit", "investment", "earnings", "principal_return", "referral_reward", "leaderboard_bonus", "withdrawal"];
const statuses = ["all", "pending", "completed", "failed"];
const label = (v, all) => (v === "all" ? all : v.replace(/_/g, " "));

export default function Transactions() {
  const [data, setData] = useState(null);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setData(null);
    const r = await api.get("/transactions", { params: { type, status, page, limit: 10 } });
    setData(r.data);
  }, [type, status, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div>
        <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">Transactions</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">Transaction History</h1>
      </div>

      <StatementExport type={type} />

      <div className="glass rounded-2xl p-6">
        <div className="flex flex-wrap gap-3 mb-5">
          <GlassSelect testId="tx-filter-type" value={type} onChange={(v) => { setType(v); setPage(1); }}
            options={types.map((t) => ({ value: t, label: label(t, "All Types") }))} className="w-44" />
          <GlassSelect testId="tx-filter-status" value={status} onChange={(v) => { setStatus(v); setPage(1); }}
            options={statuses.map((s) => ({ value: s, label: label(s, "All Status") }))} className="w-40" />
        </div>

        {data === null ? <Loader /> : data.items.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead><tr className="text-left text-slate-500 border-b border-white/5">
                  <th className="pb-3 font-medium">ID</th><th className="pb-3 font-medium">Type</th><th className="pb-3 font-medium">Amount</th><th className="pb-3 font-medium">Status</th><th className="pb-3 font-medium">Description</th><th className="pb-3 font-medium">Date</th>
                </tr></thead>
                <tbody>
                  {data.items.map((t) => (
                    <tr key={t.id} className="border-b border-white/5 last:border-0" data-testid={`tx-row-${t.id}`}>
                      <td className="py-3 font-mono text-xs text-slate-500">{t.id.slice(0, 8)}</td>
                      <td className="py-3 capitalize text-slate-200">{t.type.replace(/_/g, " ")}</td>
                      <td className="py-3 font-mono text-white">{fmt(t.amount)}</td>
                      <td className="py-3"><StatusBadge status={t.status} /></td>
                      <td className="py-3 text-slate-400 max-w-[200px] truncate">{t.description}</td>
                      <td className="py-3 text-slate-500">{fmtDateTime(t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-5">
              <p className="text-xs text-slate-500">Page {data.page} of {data.pages} · {data.total} total</p>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="tx-prev"
                  className="glass px-3 py-1.5 rounded-lg text-sm text-slate-300 disabled:opacity-40 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Prev</button>
                <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="tx-next"
                  className="glass px-3 py-1.5 rounded-lg text-sm text-slate-300 disabled:opacity-40 inline-flex items-center gap-1">Next <ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          </>
        ) : <EmptyState title="No transactions found" subtitle="Try adjusting your filters." />}
      </div>
    </div>
  );
}
